import { PaletteMode, Components, Theme } from '@mui/material';
import { CSSProperties } from 'react';
import { themePalette } from '../theme.palette';
import { typographyTokens } from '../typography-tokens';

// Define custom size interfaces
declare module '@mui/material/Link' {
  interface LinkPropsSizeOverrides {
    small: true;
    medium: true;
    large: true;
    xlarge: true;
  }
}

export const MuiLinkTheme = (mode: PaletteMode): Components<Theme>['MuiLink'] => {
  const { componentTokens, tokens } = themePalette(mode);

  const commonStyles: CSSProperties = {
    cursor: 'pointer',
    fontFamily: typographyTokens.fontFamilies.sans,
    fontSize: typographyTokens.fontSizes.base,
    lineHeight: typographyTokens.lineHeights.base,
    fontWeight: typographyTokens.fontWeights.medium,
    letterSpacing: 'normal',
    outline: 'none',
    textDecoration: 'none',
    transition: 'color 0.1s ease-out',
  };

  return {
    defaultProps: {
      underline: 'none',
    },
    styleOverrides: {
      root: {
        ...commonStyles,
        border: 'none',
        // Size variants
        '&[data-size="small"]': {
          fontSize: typographyTokens.fontSizes.xs,
          lineHeight: typographyTokens.lineHeights.xs,
        },
        '&[data-size="medium"]': {
          fontSize: typographyTokens.fontSizes.sm,
          lineHeight: typographyTokens.lineHeights.sm,
        },
        '&[data-size="large"]': {
          fontSize: typographyTokens.fontSizes.base,
          lineHeight: typographyTokens.lineHeights.base,
        },
        '&[data-size="xlarge"]': {
          fontSize: typographyTokens.fontSizes.lg,
          lineHeight: typographyTokens.lineHeights.lg,
        },
        // Prominent variant (default)
        '&[data-variant="prominent"]': {
          color: tokens.linkProminent,
          '&:hover': {
            color: tokens.linkProminentHover,
          },
          '&:focus': {
            color: tokens.linkProminentHover,
          },
          '&:active': {
            color: tokens.linkProminentHover,
          },
        },
        // Neutral variant
        '&[data-variant="neutral"]': {
          color: tokens?.linkNeutral,
          borderBottom: '1px solid',
          '&:hover': {
            color: tokens.linkNeutralHover,
          },
          '&:focus': {
            color: tokens.linkNeutralHover,
          },
          '&:active': {
            color: tokens.linkNeutralHover,
          },
        },
        // Discreet variant
        '&[data-variant="discreet"]': {
          fontSize: typographyTokens.fontSizes.xs,
          fontWeight: typographyTokens.fontWeights.bold,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          color: tokens?.linkDiscreet,
        },
        // Dark variant
        '&[data-variant="dark"]': {
          color: tokens?.linkDark,
          '&:hover': {
            color: tokens?.linkDarkHover,
          },
          '&:focus': {
            color: tokens?.linkDarkHover,
          },
          '&:active': {
            color: tokens?.linkDarkHover,
          },
        },
        // Primary variant
        '&[data-variant="primary"]': {
          color: componentTokens.buttons.colors.link.primary.text.resting,
          '&:hover': {
            color: componentTokens.buttons.colors.link.primary.text.hover,
          },
          '&:focus': {
            color: componentTokens.buttons.colors.link.primary.text.selected,
          },
          '&:active': {
            color: componentTokens.buttons.colors.link.primary.text.selected,
          },
        },
        // Secondary variant
        '&[data-variant="secondary"]': {
          color: componentTokens.buttons.colors.link.secondary.text.resting,
          '&:hover': {
            color: componentTokens.buttons.colors.link.secondary.text.hover,
          },
          '&:focus': {
            color: componentTokens.buttons.colors.link.secondary.text.selected,
          },
          '&:active': {
            color: componentTokens.buttons.colors.link.secondary.text.selected,
          },
        },
      },
    },
  };
};
